// PROPOSITO: peso, medidas y fotos de evolucion. "Lo que SI cambia" de la diapositiva 3.
// Reemplaza el Excel antropometrico y el Word de la guia de medidas.
// DEPENDENCIAS: src/lib/supabase, src/types/dominio.
// NO OBVIO (1): la escriben las dos — la alumna desde su perfil y Flor desde el panel — y siempre
// queda registrado_por. Con datos de salud, quien escribio importa tanto como el dato.
// NO OBVIO (2): el IMC se pide a la base como una columna mas ("imc"), y lo calcula la funcion
// imc(mediciones) contra la estatura de la ficha. Nunca se calcula en una pantalla: la estatura
// vive en la ficha, no en la medicion, y repetir la cuenta en el cliente es garantizar que un dia
// las dos den distinto.
// NO OBVIO (3): las fotos viven en un bucket PRIVADO. Nunca se devuelve una URL publica: se firma
// con caducidad corta y se anota el acceso.

import { supabase } from '@/lib/supabase'
import { INTERRUPTORES } from '@/config/constantes'
import { fotosDeDemostracion, medicionesDeDemostracion } from '@/services/demo/fixtures.progreso'
import type { AnguloFoto, FotoDeProgreso, Medicion } from '@/types/dominio'

const MINUTOS_DE_FIRMA = 5

export interface MedicionAEscribir {
  clienteId: string
  alumnaId: string
  fecha: string
  pesoKg: number | null
  grasaPct: number | null
  cinturaCm: number | null
  caderaCm: number | null
  musloDerCm: number | null
  musloIzqCm: number | null
  brazoDerCm: number | null
  brazoIzqCm: number | null
  nota: string | null
  registradoPor: string
}

export async function registrarMedicion(m: MedicionAEscribir): Promise<void> {
  if (INTERRUPTORES.demo) return

  const { error } = await supabase.from('mediciones').upsert(
    {
      alumna_id: m.alumnaId,
      fecha: m.fecha,
      peso_kg: m.pesoKg,
      grasa_pct: m.grasaPct,
      cintura_cm: m.cinturaCm,
      cadera_cm: m.caderaCm,
      muslo_der_cm: m.musloDerCm,
      muslo_izq_cm: m.musloIzqCm,
      brazo_der_cm: m.brazoDerCm,
      brazo_izq_cm: m.brazoIzqCm,
      nota: m.nota,
      registrado_por: m.registradoPor,
    },
    { onConflict: 'alumna_id,fecha' },
  )
  if (error) throw error
}

export async function traerMediciones(alumnaId: string): Promise<Medicion[]> {
  if (INTERRUPTORES.demo) return medicionesDeDemostracion()

  const { data, error } = await supabase
    .from('mediciones')
    .select(
      'id, fecha, peso_kg, imc, grasa_pct, cintura_cm, cadera_cm, muslo_der_cm, muslo_izq_cm, brazo_der_cm, brazo_izq_cm, nota, registrado_por',
    )
    .eq('alumna_id', alumnaId)
    .is('deleted_at', null)
    .order('fecha', { ascending: true })

  if (error) throw error
  return data.map(f => ({
    id: f.id,
    fecha: f.fecha,
    pesoKg: f.peso_kg,
    imc: f.imc,
    grasaPct: f.grasa_pct,
    cinturaCm: f.cintura_cm,
    caderaCm: f.cadera_cm,
    musloDerCm: f.muslo_der_cm,
    musloIzqCm: f.muslo_izq_cm,
    brazoDerCm: f.brazo_der_cm,
    brazoIzqCm: f.brazo_izq_cm,
    registradoPor: f.registrado_por,
    nota: f.nota,
  }))
}

/** Sube una foto al bucket privado. La ruta empieza por el id de la alumna: eso es lo que la RLS mira. */
export async function subirFoto(
  alumnaId: string,
  fecha: string,
  angulo: AnguloFoto,
  archivo: File,
  registradoPor: string,
): Promise<void> {
  const ruta = `${alumnaId}/${fecha}/${angulo}.webp`
  const subida = await supabase.storage
    .from('fotos-progreso')
    .upload(ruta, archivo, { upsert: true, contentType: archivo.type })
  if (subida.error) throw subida.error

  const { error } = await supabase.from('fotos_progreso').upsert(
    {
      alumna_id: alumnaId,
      fecha,
      angulo,
      ruta_storage: ruta,
      registrado_por: registradoPor,
    },
    { onConflict: 'alumna_id,fecha,angulo' },
  )
  if (error) throw error
}

export async function traerFotos(alumnaId: string): Promise<FotoDeProgreso[]> {
  if (INTERRUPTORES.demo) return fotosDeDemostracion()

  const { data, error } = await supabase
    .from('fotos_progreso')
    .select('id, fecha, angulo, ruta_storage, visible_para_entrenadora')
    .eq('alumna_id', alumnaId)
    .is('deleted_at', null)
    .order('fecha', { ascending: false })

  if (error) throw error
  return data.map(f => ({
    id: f.id,
    fecha: f.fecha,
    angulo: f.angulo,
    rutaStorage: f.ruta_storage,
    visibleParaEntrenadora: f.visible_para_entrenadora,
  }))
}

/** URL temporal para MOSTRAR una foto. Caduca en minutos: nunca se guarda ni se comparte. */
export async function firmarFoto(ruta: string): Promise<string | null> {
  // En demo no hay bucket: la pantalla dibuja un marcador en vez de una foto real.
  if (INTERRUPTORES.demo) return null

  const { data } = await supabase.storage
    .from('fotos-progreso')
    .createSignedUrl(ruta, MINUTOS_DE_FIRMA * 60)
  return data?.signedUrl ?? null
}
